import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper";
import Animation from "@areas/shared/components/chooseLayout/Animation";
import Honeycombs from "@areas/shared/components/background/Honeycombs";
import { fetchWrapper } from "@helpers/fetchwrapper";

import styles from "./styles/promtours.module.css";

const DEFAULT_PROMTOUR_IMAGE = "/static/media/default-company.a1b57740.jpg";

const PROMTOUR_TYPES = {
  1: "Профориентационный тур",
  2: "День открытых дверей",
  3: "Экскурсия",
};

function getCompanyName(company) {
  return (
    company?.Shortname ||
    company?.Fullname ||
    company?.NameCompany ||
    ""
  );
}

function getCompanyAddress(company) {
  return company?.Addresses?.[0]?.Value || "";
}

function getCompanyContact(company, idType) {
  const contact = company?.Contacts?.find((item) => item.IdType === idType);
  return contact?.Value || "";
}

function PromtourImage({ item }) {
  const [src, setSrc] = useState(() =>
    item.Image
      ? `/v1/api/fileManager/getFile?path=${item.Image}`
      : DEFAULT_PROMTOUR_IMAGE
  );

  useEffect(() => {
    setSrc(
      item.Image
        ? `/v1/api/fileManager/getFile?path=${item.Image}`
        : DEFAULT_PROMTOUR_IMAGE
    );
  }, [item.Id, item.Image]);

  const handleError = () => {
    setSrc((current) =>
      current !== DEFAULT_PROMTOUR_IMAGE ? DEFAULT_PROMTOUR_IMAGE : current
    );
  };

  return (
    <img src={src} alt={item.Title || "Профтур"} onError={handleError} />
  );
}

function Promtours() {
  const [items, setItems] = useState([]);
  const [load, setLoad] = useState(true);
  const [swiper, setSwiper] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadPromtours = async () => {
      setLoad(true);

      try {
        const response = await fetchWrapper.get(
          "/v1/api/app/GetCompanyPromtours?offset=0&limit=10"
        );
        const promtours = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

        if (promtours.length === 0) {
          if (!cancelled) {
            setItems([]);
            setLoad(false);
          }
          return;
        }

        const companyIds = [
          ...new Set(
            promtours
              .filter((item) => !item.IdCompanyNavigation && item.IdCompany)
              .map((item) => item.IdCompany)
          ),
        ];

        const companies = await Promise.all(
          companyIds.map((id) =>
            fetchWrapper.get(`/v1/api/app/company/${id}`).catch(() => null)
          )
        );

        const companyMap = companyIds.reduce((acc, id, index) => {
          acc[id] = companies[index];
          return acc;
        }, {});

        if (!cancelled) {
          setItems(
            promtours.map((item) => ({
              ...item,
              company:
                item.IdCompanyNavigation || companyMap[item.IdCompany] || null,
            }))
          );
          setLoad(false);
        }
      } catch {
        if (!cancelled) {
          setItems([]);
          setLoad(false);
        }
      }
    };

    loadPromtours();

    return () => {
      cancelled = true;
    };
  }, []);

  if (load || items.length === 0) {
    return null;
  }

  return (
    <Animation>
      <div className={`${styles.block} container`}>
        <div className={styles.header}>Профтуры</div>
        <Honeycombs style={styles.background} />

        <div className={styles.slider}>
          {items.length > 1 ? (
            <button
              type="button"
              className={`${styles.nav} ${styles.navPrev}`}
              onClick={() => swiper?.slidePrev()}
              aria-label="Предыдущий профтур"
            >
              ←
            </button>
          ) : null}

          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true }}
            slidesPerView={1}
            spaceBetween={24}
            loop={items.length > 1}
            className={styles.swiper}
            onSwiper={setSwiper}
          >
            {items.map((item) => (
              <SwiperSlide key={item.Id} className={styles.slide}>
                <div className={styles.card}>
                  <div className={styles.image}>
                    <PromtourImage item={item} />
                  </div>

                  <div className={styles.info}>
                    <div className={styles.section}>
                      <div className={styles.sectionTitle}>Профтур</div>
                      <div className={styles.promtourTitle}>{item.Title}</div>
                      {item.Description ? (
                        <div className={styles.description}>
                          {item.Description}
                        </div>
                      ) : null}
                      <div className={styles.meta}>
                        {PROMTOUR_TYPES[item.IdType] ? (
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Тип:</span>
                            {PROMTOUR_TYPES[item.IdType]}
                          </div>
                        ) : null}
                        {item.Industry ? (
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Отрасль:</span>
                            {item.Industry}
                          </div>
                        ) : null}
                        {item.City ? (
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>
                              Город/район:
                            </span>
                            {item.City}
                          </div>
                        ) : null}
                        {item.TargetGroup ? (
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>
                              Целевая группа:
                            </span>
                            {item.TargetGroup}
                          </div>
                        ) : null}
                        {item.ParticipantsCount !== null &&
                        item.ParticipantsCount !== undefined ? (
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>
                              Участники:
                            </span>
                            {item.ParticipantsCount}
                          </div>
                        ) : null}
                        {item.ContactPerson ? (
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>
                              Контактное лицо:
                            </span>
                            {item.ContactPerson}
                          </div>
                        ) : null}
                        {item.Address ? (
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Адрес:</span>
                            {item.Address}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {item.company ? (
                      <div className={styles.section}>
                        <div className={styles.sectionTitle}>Компания</div>
                        <div className={styles.companyName}>
                          {getCompanyName(item.company)}
                        </div>
                        <div className={styles.companyDetails}>
                          {getCompanyAddress(item.company) ? (
                            <div className={styles.metaItem}>
                              <span className={styles.metaLabel}>Адрес:</span>
                              {getCompanyAddress(item.company)}
                            </div>
                          ) : null}
                          {getCompanyContact(item.company, 2) ? (
                            <div className={styles.metaItem}>
                              <span className={styles.metaLabel}>Телефон:</span>
                              {getCompanyContact(item.company, 2)}
                            </div>
                          ) : null}
                          {getCompanyContact(item.company, 1) ? (
                            <div className={styles.metaItem}>
                              <span className={styles.metaLabel}>Email:</span>
                              {getCompanyContact(item.company, 1)}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    {item.company?.Slug ? (
                      <Link
                        to={`/company/${item.company.Slug}?tab=promtours`}
                        target="_blank"
                        className={styles.more}
                      >
                        На страницу компании
                      </Link>
                    ) : null}
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {items.length > 1 ? (
            <button
              type="button"
              className={`${styles.nav} ${styles.navNext}`}
              onClick={() => swiper?.slideNext()}
              aria-label="Следующий профтур"
            >
              →
            </button>
          ) : null}
        </div>
      </div>
    </Animation>
  );
}

export default Promtours;
